import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { WorkforceplanMovementType } from "services/enums/WorkforceplanMovementType.enum";
import { WorkforcePlanApprovalDetails } from "services/models/workforceplan/workforceplan-solicitation-details.model";
import {
	DrawerDataInput,
	FormRequestWorkforcePlanPayload,
	FormRequestWorkforcePlanValues,
	ISolicitationFormComponent,
} from "../drawer-workforce-plan-solicitation/form-types";
import {
	WorkforcePlanAmountModel,
	WorkforcePlanWorkScaleGroupModel,
} from "services/models/workforceplan";
import { WorkforcePlanSolicitationStepModel } from "services/models/workforceplan/workforceplan-solicitation.model";

@Component({
	selector: "app-drawer-workforceplan-solicitation-planning",
	templateUrl: "./drawer-workforce-plan-solicitation-planning.component.html",
	styleUrls: ["./drawer-workforce-plan-solicitation-planning.component.scss"],
})
export class AppSharedDrawerWorkforceplanSolicitationPlanningComponent
	implements OnChanges, ISolicitationFormComponent {
	@Input() data: DrawerDataInput | null = null;

	@Input() source: "action-required" | null = null;

	isPreviousObservationsCollapsed = true;

	form: UntypedFormGroup;

	constructor(private _formBuilder: FormBuilder) {
		this.form = this._buildEmptyForm();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes["data"]?.currentValue || !this.data) {
			return;
		}

		if (this.data.mode === "add" && this.data.type === "REQUEST_WORK_FORCEPLAN") {
			this._buildFormForAdd();
			return;
		}

		if (this.data.mode === "details" && this._isPlanningDetailsType()) {
			this._buildFormForDetails();
		}
	}

	getForm(): UntypedFormGroup {
		return this.form;
	}

	public get noteControl(): FormControl {
		return this.form.get("note") as FormControl;
	}

	getPayload(): FormRequestWorkforcePlanPayload {
		const { note, movementType, workforcePlanId } = this.form.value as FormRequestWorkforcePlanValues;

		return {
			note,
			movementType,
			workforcePlanId,
		};
	}

	public getJobLabel(value: WorkforcePlanAmountModel) {
		if (value?.jobTitleName) {
			return `${value?.jobTitleGroupName} / ${value?.jobTitleName}`;
		}

		return `${value?.jobTitleGroupName}`;
	}

	public getTotalHadCount(workScale: WorkforcePlanWorkScaleGroupModel) {
		return workScale.amounts.reduce((acc, item) => acc + (item?.newAmount ?? 0), 0);
	}

	public isAmountAdded(item: WorkforcePlanAmountModel): boolean {
		if (item.action === "added") {
			return true;
		}

		return this._hasLegacyChangeDirection(item, "added");
	}

	public isAmountRemoved(item: WorkforcePlanAmountModel): boolean {
		if (item.action === "removed") {
			return true;
		}

		return this._hasLegacyChangeDirection(item, "removed");
	}

	public showsPreviousAmount(item: WorkforcePlanAmountModel): boolean {
		return (
			item.changeDirection === "increase" ||
			item.changeDirection === "decrease" ||
			this.isAmountRemoved(item)
		);
	}

	public getAmountCssClass(item: WorkforcePlanAmountModel): string {
		if (this.isAmountAdded(item)) {
			return "added";
		}
		if (this.isAmountRemoved(item)) {
			return "removed";
		}

		return item.changeDirection ?? "";
	}

	public get workScaleGroups() {
		return this.data?.solicitationDetails?.approvalDetails?.workScaleGroups;
	}

	public get approvalDetails(): WorkforcePlanApprovalDetails | undefined {
		return this.data?.solicitationDetails?.approvalDetails;
	}

	public get totalPlanned() {
		return this.workScaleGroups?.reduce(
			(acc, group) => {
				group.amounts.forEach(amount => {
					acc += amount.newAmount ?? 0;
				});
				return acc;
			},
			0
		);
	}

	public get totalEffective(): number {
		const fromApi = this.approvalDetails?.totalEffective;
		if (fromApi != null) {
			return fromApi;
		}

		return this.workScaleGroups?.reduce(
			(acc, group) => {
				group.amounts.forEach(amount => {
					acc += amount.previousAmount ?? 0;
				});
				return acc;
			},
			0
		) ?? 0;
	}

	public get proposalChangeDirection(): "increase" | "decrease" | "unchanged" {
		const planned = this.totalPlanned ?? 0;
		const previous = this.totalEffective;

		if (planned > previous) {
			return "increase";
		}

		if (planned < previous) {
			return "decrease";
		}

		return "unchanged";
	}

	public get previousStepObservations(): WorkforcePlanSolicitationStepModel[] {
		const steps = this.data?.solicitation?.steps ?? [];
		const currentSequence = this._currentStepSequence;

		return steps
			.filter(step =>
				step.status === "APPROVED"
				&& !!step.note?.trim()
				&& step.sequence < currentSequence
			)
			.sort((a, b) => a.sequence - b.sequence);
	}

	public get hasPreviousStepObservations(): boolean {
		return this.previousStepObservations.length > 0;
	}

	public togglePreviousObservations(): void {
		this.isPreviousObservationsCollapsed = !this.isPreviousObservationsCollapsed;
	}

	public trackByStepId(_index: number, step: WorkforcePlanSolicitationStepModel): string {
		if (step.id) {
			return step.id;
		}

		if (step.approvalScopeItemId) {
			return `${step.approvalScopeItemId}-${step.sequence}`;
		}

		return `step-${step.sequence}`;
	}

	public getObservationStepLabel(step: WorkforcePlanSolicitationStepModel): string {
		const stepName = step.stepName?.trim();
		if (stepName) {
			return stepName;
		}

		if (step.sequence > 0) {
			return `Etapa ${step.sequence}`;
		}

		return "Etapa não informada";
	}

	public getObservationApproverLabel(step: WorkforcePlanSolicitationStepModel): string {
		const approverName = step.approverName?.trim();
		return approverName || "Aprovador não informado";
	}

	public getObservationApproverImage(step: WorkforcePlanSolicitationStepModel): string {
		const approverImage = step.approverImage?.trim();
		return approverImage || "assets/images/logo/avatar-profile.png";
	}

	public getObservationNote(step: WorkforcePlanSolicitationStepModel): string {
		const note = step.note?.trim();
		return note || "Sem observação";
	}

	private get _currentStepSequence(): number {
		const steps = this.data?.solicitation?.steps ?? [];
		if (!steps.length) {
			return Number.POSITIVE_INFINITY;
		}

		const pendingStep = steps.find(step => step.status === "NONE" || step.status === "PENDING");
		if (pendingStep) {
			return pendingStep.sequence;
		}

		const currentScopeItemId =
			this.data?.solicitationDetails?.approvalScopeItemId
			|| this.data?.solicitation?.approvalScopeItemId;

		if (currentScopeItemId) {
			const matchedStep = steps.find(step => step.approvalScopeItemId === currentScopeItemId);
			if (matchedStep) {
				return matchedStep.sequence;
			}
		}

		return Number.POSITIVE_INFINITY;
	}

	private _hasLegacyChangeDirection(
		item: WorkforcePlanAmountModel,
		direction: "added" | "removed"
	): boolean {
		return (item.changeDirection as string | undefined) === direction;
	}

	private _isPlanningDetailsType(): boolean {
		return this.data?.type === "REQUEST_WORK_FORCEPLAN";
	}

	private _buildEmptyForm(): UntypedFormGroup {
		return this._formBuilder.group({});
	}

	private _buildFormForAdd(): void {
		this.form = this._formBuilder.group({
			note: [null, Validators.required],
			movementType: [WorkforceplanMovementType.WORK_FORCEPLAN.id],
			workforcePlanId: [this.data?.workforcePlanId],
		});
	}

	private _buildFormForDetails(): void {
		this.form = this._buildEmptyForm();
	}
}
